/**
 * Chat Read States
 *
 * Persists per-user read cursors for group and shared chat experiences.
 *
 * Primary responsibilities:
 * - Read the current user's last chat read cursor
 * - Advance the cursor without moving it backwards
 * - Report whether a message was opened for the first time
 *
 * Dependencies:
 * - Supabase service client
 */

import type { SupabaseClient } from '@supabase/supabase-js'

import { createServiceClient } from '@/lib/supabase/service-client'
import type { Database } from '@/supabase/types'

type ChatReadStateTable = {
  Row: {
    chat_id: string
    user_id: string
    last_read_message_id: string | null
    last_read_sequence_number: number
    last_read_at: string
  }
  Insert: {
    chat_id: string
    user_id: string
    last_read_message_id?: string | null
    last_read_sequence_number?: number
    last_read_at?: string
  }
  Update: {
    chat_id?: string
    user_id?: string
    last_read_message_id?: string | null
    last_read_sequence_number?: number
    last_read_at?: string
  }
  Relationships: []
}

type ChatReadStatesDatabase = Omit<Database, 'public'> & {
  public: Omit<Database['public'], 'Tables'> & {
    Tables: Database['public']['Tables'] & {
      chat_read_states: ChatReadStateTable
    }
  }
}

type ChatReadStatesClient = SupabaseClient<ChatReadStatesDatabase>

export type UpsertChatReadStateResult = {
  firstRead: boolean
  lastReadSequenceNumber: number
}

function getChatReadStatesClient(
  client?: SupabaseClient<Database>
): ChatReadStatesClient {
  return (client ?? createServiceClient()) as unknown as ChatReadStatesClient
}

// --------------

export async function upsertChatReadState({
  chatId,
  userId,
  messageId,
  sequenceNumber,
  client
}: {
  chatId: string
  userId: string
  messageId: string
  sequenceNumber: number
  client?: SupabaseClient<Database>
}): Promise<UpsertChatReadStateResult> {
  const db = getChatReadStatesClient(client)
  const { data: currentState, error: currentError } = await db
    .from('chat_read_states')
    .select('last_read_sequence_number')
    .eq('chat_id', chatId)
    .eq('user_id', userId)
    .maybeSingle()

  if (currentError) {
    throw new Error(currentError.message)
  }

  const currentSequenceNumber = Number(currentState?.last_read_sequence_number)
  const hasReadMessage =
    Number.isFinite(currentSequenceNumber) &&
    currentSequenceNumber >= sequenceNumber

  if (hasReadMessage) {
    return {
      firstRead: false,
      lastReadSequenceNumber: currentSequenceNumber
    }
  }

  const { error: upsertError } = await db
    .from('chat_read_states')
    .upsert(
      {
        chat_id: chatId,
        user_id: userId,
        last_read_message_id: messageId,
        last_read_sequence_number: sequenceNumber,
        last_read_at: new Date().toISOString()
      },
      {
        onConflict: 'chat_id,user_id'
      }
    )

  if (upsertError) {
    throw new Error(upsertError.message)
  }

  return {
    firstRead: true,
    lastReadSequenceNumber: sequenceNumber
  }
}

// --------------

export async function getChatReadStateSequenceNumbers({
  chatIds,
  userId,
  client
}: {
  chatIds: string[]
  userId: string
  client?: SupabaseClient<Database>
}): Promise<Map<string, number>> {
  const normalizedChatIds = Array.from(
    new Set(chatIds.map((chatId) => chatId.trim()).filter(Boolean))
  )

  if (normalizedChatIds.length === 0 || !userId.trim()) {
    return new Map()
  }

  const db = getChatReadStatesClient(client)
  const { data, error } = await db
    .from('chat_read_states')
    .select('chat_id, last_read_sequence_number')
    .eq('user_id', userId)
    .in('chat_id', normalizedChatIds)

  if (error) {
    throw new Error(error.message)
  }

  return new Map(
    (data ?? []).flatMap((state) => {
      const sequenceNumber = Number(state.last_read_sequence_number)

      return Number.isFinite(sequenceNumber)
        ? [[state.chat_id, sequenceNumber] as const]
        : []
    })
  )
}
